import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { auth, db, googleProvider } from '../firebase';
import {
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  onAuthStateChanged,
  browserPopupRedirectResolver,
} from 'firebase/auth';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { ensureHrBootstrapProfile, shouldGrantHrBootstrap } from '../services/hrBootstrapService';
import { normalizeHeadDepartmentsFromUser } from '../services/userDb';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const FIRESTORE_OP_MS = 12000;
const AUTH_STUCK_MS = 10000;
const PROFILE_LOADING_FALLBACK_MS = 18000;

/**
 * Firestore reads race against a deadline so getDoc cannot hang the UI forever.
 * (DevTools mobile emulation can throttle setTimeout; combine with PROFILE_LOADING + tab/online retry.)
 */
function raceFirestore(promise) {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(
        () => reject(Object.assign(new Error('firestore-deadline'), { code: 'firestore-deadline' })),
        FIRESTORE_OP_MS
      )
    ),
  ]);
}

/** Single place: Firestore users + pending_users → app user shape. */
function userStateFromFirestoreDocs(userSnap, pendingSnap, authUser) {
  if (!authUser?.uid || !authUser.email) return null;
  if (userSnap.exists()) {
    const userData = userSnap.data();
    const newRole = (userData.role || 'employee').toUpperCase();
    return {
      uid: authUser.uid,
      email: authUser.email,
      ...userData,
      role: newRole,
    };
  }
  if (pendingSnap.exists()) {
    return {
      uid: authUser.uid,
      email: authUser.email,
      ...pendingSnap.data(),
      role: 'PENDING',
    };
  }
  return {
    uid: authUser.uid,
    email: authUser.email,
    name: authUser.displayName || authUser.email.split('@')[0],
    role: 'NEED_DEPT',
  };
}

/**
 * React 18 Strict Mode runs effects twice in development. Two overlapping
 * getRedirectResult() calls can race and surface SDK errors (e.g. auth/argument-error).
 * One consume per full page load is enough.
 */
let googleRedirectResultConsumeStarted = false;

/**
 * Phones / in-app browsers: popup often never appears or is blocked — use full-page redirect.
 * Desktop (non-localhost): popup first, then redirect on block / unsupported.
 */
function shouldUseGoogleRedirect() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/Instagram|FBAN|FBAV|FB_IAB|Line\//i.test(ua)) return true;
  if (typeof window !== 'undefined') {
    const h = window.location.hostname;
    if (h === 'localhost' || h === '127.0.0.1') return false;
  }
  if (/iPhone|iPad|iPod|Android|Mobile Safari|CriOS|SamsungBrowser/i.test(ua)) return true;
  try {
    if (navigator.userAgentData?.mobile === true) return true;
  } catch {
    /* not available */
  }
  // Narrow touch UIs (real phones + Chrome device toolbar): avoid popup entirely.
  try {
    if (typeof window !== 'undefined' && window.matchMedia) {
      const narrow = window.matchMedia('(max-width: 900px)').matches;
      const coarse = window.matchMedia('(pointer: coarse)').matches;
      const touch = (navigator.maxTouchPoints || 0) > 0;
      if (narrow && (touch || coarse)) return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

async function finalizeGoogleLogin(user) {
  if (!user?.email || !db) throw new Error('Firebase is not available');
  const userRef = doc(db, 'users', user.email);
  const pendingRef = doc(db, 'pending_users', user.email);
  let userSnap;
  let pendingSnap;
  try {
    [userSnap, pendingSnap] = await raceFirestore(
      Promise.all([getDoc(userRef), getDoc(pendingRef)])
    );
  } catch (e) {
    if (e?.code === 'firestore-deadline') throw new Error('NEED_DEPT');
    throw e;
  }

  if (!userSnap.exists() && pendingSnap.exists()) {
    const st = (pendingSnap.data()?.status || '').toUpperCase();
    if (st === 'REJECTED') throw new Error('NEED_DEPT');
  }

  if (!userSnap.exists() && !pendingSnap.exists()) {
    let grant = false;
    try {
      grant = await raceFirestore(shouldGrantHrBootstrap(user.email));
    } catch {
      grant = false;
    }
    if (grant) {
      try {
        await raceFirestore(ensureHrBootstrapProfile(user));
      } catch {
        /* offline / timeout — still let app continue */
      }
      return;
    }
    throw new Error('NEED_DEPT');
  }
}

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [googleLoginHint, setGoogleLoginHint] = useState(null);
  const [redirectLoginError, setRedirectLoginError] = useState(null);
  const profileHintRef = useRef({ uid: null, role: null });

  useEffect(() => {
    profileHintRef.current = {
      uid: currentUser?.uid ?? null,
      role: currentUser?.role ?? null,
    };
  }, [currentUser?.uid, currentUser?.role]);

  /** When tab becomes visible or network returns, re-fetch profile if we never left loading state. */
  useEffect(() => {
    if (!auth || !db) return undefined;
    let cancelled = false;
    const tryHydrateFromServer = async () => {
      const u = auth.currentUser;
      const { uid, role } = profileHintRef.current;
      if (!u?.email || !uid || u.uid !== uid) return;
      if (role !== 'PROFILE_LOADING' && role !== 'NEED_DEPT') return;
      try {
        const userRef = doc(db, 'users', u.email);
        const pendingRef = doc(db, 'pending_users', u.email);
        const [userSnap, pendingSnap] = await raceFirestore(
          Promise.all([getDoc(userRef), getDoc(pendingRef)])
        );
        if (cancelled || auth.currentUser?.uid !== u.uid) return;
        const next = userStateFromFirestoreDocs(userSnap, pendingSnap, u);
        if (next) {
          setCurrentUser((prev) => (prev?.uid !== u.uid ? prev : next));
        }
      } catch {
        /* still offline / deadline */
      }
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') void tryHydrateFromServer();
    };
    const onOnline = () => void tryHydrateFromServer();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
    };
  }, [auth, db]);

  useEffect(() => {
    if (!auth || !db) {
      console.error(
        'Firebase Auth or Firestore failed to initialize. Check firebase.js / env / network.'
      );
      setLoading(false);
      return;
    }

    let unsubs = [];

    if (!googleRedirectResultConsumeStarted) {
      googleRedirectResultConsumeStarted = true;
      (async () => {
        try {
          const result = await getRedirectResult(auth, browserPopupRedirectResolver);
          if (!result?.user) return;
          await finalizeGoogleLogin(result.user);
          setGoogleLoginHint(null);
          setRedirectLoginError(null);
        } catch (e) {
          if (e?.message === 'NEED_DEPT') {
            setGoogleLoginHint('NEED_DEPT');
          } else if (e?.code && e.code !== 'auth/popup-closed-by-user') {
            console.error('Google redirect sign-in:', e?.code, e?.message, e);
            const msg = `${e?.message || ''}`;
            const missingState =
              msg.includes('missing initial state') || msg.includes('storage-partitioned');
            setRedirectLoginError(
              e?.code === 'auth/unauthorized-domain'
                ? 'This website address is not authorized for Google sign-in. Firebase Console → Authentication → Settings → Authorized domains — add your exact site host (e.g. your-app.web.app or LAN IP for testing).'
                : e?.code === 'auth/argument-error'
                  ? 'Sign-in failed (auth/argument-error). Try again; if it persists, use the exact site URL that matches Firebase (REACT_APP_FIREBASE_AUTH_DOMAIN / authorized domains) and a normal browser tab—not an in-app browser.'
                  : missingState
                    ? 'Sign-in could not finish (browser blocked redirect state). Use the same site URL as in Firebase (set REACT_APP_FIREBASE_AUTH_DOMAIN to your live domain, e.g. hr.acadxl.com), add that domain under Authentication → Authorized domains, and connect it in Firebase Hosting so Google can return to this app reliably. Try again in a normal browser tab, not an in-app browser.'
                    : `${e?.message || e?.code || 'Sign-in failed after Google.'}`
            );
          } else if (e?.message) {
            setRedirectLoginError(e.message);
          } else {
            setRedirectLoginError(
              'Google sign-in did not finish. Use normal Chrome/Safari (not Instagram/WhatsApp in-app browser), and add this exact site domain under Firebase → Authentication → Authorized domains.'
            );
          }
        }
      })();
    }

    let authEffectCancelled = false;
    let profileLoadGen = 0;
    let authStateDelivered = false;
    const authStuckTimer = setTimeout(() => {
      if (authEffectCancelled || authStateDelivered) return;
      console.warn('Auth: onAuthStateChanged delayed; unblocking shell.');
      setLoading(false);
    }, AUTH_STUCK_MS);

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (!authStateDelivered) {
        authStateDelivered = true;
        clearTimeout(authStuckTimer);
      }
      unsubs.forEach((unsub) => unsub());
      unsubs = [];

      if (user) {
        const loadGen = ++profileLoadGen;
        setCurrentUser({
          uid: user.uid,
          email: user.email,
          name: user.displayName || user.email?.split('@')[0] || '',
          role: 'PROFILE_LOADING',
        });
        setLoading(false);

        const applyUserFromSnaps = (userSnap, pendingSnap, authUser) => {
          const next = userStateFromFirestoreDocs(userSnap, pendingSnap, authUser);
          if (next) setCurrentUser(next);
        };

        void (async () => {
          try {
            await raceFirestore(ensureHrBootstrapProfile(user));
          } catch (e) {
            if (e?.code === 'firestore-deadline') {
              console.warn('HR bootstrap skipped (Firestore deadline).');
            } else {
              console.error('HR bootstrap profile failed:', e);
            }
          }
          if (loadGen !== profileLoadGen || authEffectCancelled || !auth.currentUser || auth.currentUser.uid !== user.uid) {
            return;
          }

          const userEmail = user.email;
          const userRef = doc(db, 'users', userEmail);
          const pendingRef = doc(db, 'pending_users', userEmail);

          try {
            const [userSnap, pendingSnap] = await raceFirestore(
              Promise.all([getDoc(userRef), getDoc(pendingRef)])
            );
            if (loadGen !== profileLoadGen || authEffectCancelled || !auth.currentUser || auth.currentUser.uid !== user.uid) return;
            applyUserFromSnaps(userSnap, pendingSnap, auth.currentUser);
            setGoogleLoginHint(null);
          } catch (e) {
            if (e?.code === 'firestore-deadline') {
              console.warn('Initial profile read timed out; using NEED_DEPT fallback until online.');
            } else {
              console.error('Initial user documents load failed:', e);
            }
            if (loadGen === profileLoadGen && !authEffectCancelled && auth.currentUser?.uid === user.uid) {
              setCurrentUser({
                uid: user.uid,
                email: user.email,
                name: user.displayName || user.email.split('@')[0],
                role: 'NEED_DEPT',
              });
            }
          }

          setTimeout(() => {
            if (loadGen !== profileLoadGen || authEffectCancelled || !auth.currentUser || auth.currentUser.uid !== user.uid) return;

            const unsubUser = onSnapshot(
              userRef,
              (docSnap) => {
                if (docSnap.exists()) {
                  const userData = docSnap.data();
                  const newRole = (userData.role || 'employee').toUpperCase();
                  setCurrentUser((prev) => {
                    const prevHead = JSON.stringify(normalizeHeadDepartmentsFromUser(prev));
                    const nextHead = JSON.stringify(normalizeHeadDepartmentsFromUser(userData));
                    if (
                      prev?.role === newRole &&
                      JSON.stringify(prev?.department) === JSON.stringify(userData.department) &&
                      prevHead === nextHead
                    ) {
                      return prev;
                    }
                    return {
                      uid: user.uid,
                      email: user.email,
                      ...userData,
                      role: newRole,
                    };
                  });
                } else {
                  raceFirestore(getDoc(pendingRef))
                    .then((pSnap) => {
                      if (pSnap.exists()) {
                        setCurrentUser({ uid: user.uid, email: user.email, ...pSnap.data(), role: 'PENDING' });
                      } else if (auth.currentUser?.uid === user.uid) {
                        setCurrentUser({
                          uid: user.uid,
                          email: user.email,
                          name: user.displayName || user.email.split('@')[0],
                          role: 'NEED_DEPT',
                        });
                      }
                    })
                    .catch(() => {});
                }
              },
              (err) => {
                console.error('users/{email} snapshot error:', err);
              }
            );
            unsubs.push(unsubUser);

            const unsubPending = onSnapshot(
              pendingRef,
              (pendingSnap) => {
                if (pendingSnap.exists()) {
                  const pendData = pendingSnap.data();
                  setCurrentUser((prev) => {
                    if (prev?.role && prev.role !== 'PENDING') return prev;
                    return { uid: user.uid, email: user.email, ...pendData, role: 'PENDING' };
                  });
                }
              },
              (err) => {
                console.error('pending_users snapshot error:', err);
              }
            );
            unsubs.push(unsubPending);
          }, 50);
        })();
      } else {
        profileLoadGen += 1;
        setCurrentUser(null);
        setLoading(false);
      }
    });

    return () => {
      authEffectCancelled = true;
      clearTimeout(authStuckTimer);
      unsubscribeAuth();
      unsubs.forEach((unsub) => unsub());
    };
  }, []);

  useEffect(() => {
    if (currentUser?.role !== 'PROFILE_LOADING') return undefined;
    const t = setTimeout(() => {
      setCurrentUser((prev) =>
        prev?.role === 'PROFILE_LOADING'
          ? { ...prev, role: 'NEED_DEPT' }
          : prev
      );
    }, PROFILE_LOADING_FALLBACK_MS);
    return () => clearTimeout(t);
  }, [currentUser?.uid, currentUser?.role]);

  const loginWithGoogle = async () => {
    if (!auth) throw new Error('Firebase Auth is not available');
    setRedirectLoginError(null);
    if (shouldUseGoogleRedirect()) {
      try {
        await signInWithRedirect(auth, googleProvider, browserPopupRedirectResolver);
      } catch (e) {
        console.error('signInWithRedirect error:', e);
        setRedirectLoginError(e?.message || 'Could not start Google sign-in.');
        throw e;
      }
      return null;
    }
    try {
      const result = await signInWithPopup(auth, googleProvider);
      await finalizeGoogleLogin(result.user);
      return result;
    } catch (error) {
      if (
        error?.code === 'auth/popup-blocked' ||
        error?.code === 'auth/cancelled-popup-request' ||
        error?.code === 'auth/operation-not-supported-in-this-environment'
      ) {
        await signInWithRedirect(auth, googleProvider, browserPopupRedirectResolver);
        return null;
      }
      if (error.message !== 'NEED_DEPT') {
        console.error('Login failed:', error);
      }
      throw error;
    }
  };

  const logout = () => {
    setGoogleLoginHint(null);
    if (!auth) return Promise.resolve();
    return signOut(auth);
  };

  const completeRegistration = async (department) => {
    if (!auth || !db) throw new Error('Firebase is not available');
    const user = auth.currentUser;
    if (!user) throw new Error('No user authenticated');
    const pendingRef = doc(db, 'pending_users', user.email);
    await setDoc(pendingRef, {
      email: user.email,
      name: user.displayName || user.email.split('@')[0],
      status: 'PENDING',
      department: department,
      createdAt: new Date().toISOString(),
    });
    setGoogleLoginHint(null);
  };

  const value = {
    currentUser,
    loginWithGoogle,
    completeRegistration,
    logout,
    googleLoginHint,
    clearGoogleLoginHint: () => setGoogleLoginHint(null),
    redirectLoginError,
    clearRedirectLoginError: () => setRedirectLoginError(null),
  };

  const firebaseReady = !!(auth && db);

  return (
    <AuthContext.Provider value={value}>
      {!firebaseReady && !loading ? (
        <div style={{ display: 'flex', height: '100vh', justifyContent: 'center', alignItems: 'center', background: '#f4f5f7', padding: 24 }}>
          <div style={{ textAlign: 'center', maxWidth: 420 }}>
            <div style={{ fontSize: '18px', fontWeight: '700', color: '#c0392b', marginBottom: '8px' }}>Configuration error</div>
            <div style={{ fontSize: '14px', color: '#566070', lineHeight: 1.5 }}>
              Firebase did not initialize. Confirm your <code>REACT_APP_FIREBASE_*</code> env vars and that the app is not blocked by a browser extension.
            </div>
          </div>
        </div>
      ) : !loading ? (
        children
      ) : (
        <div style={{ display: 'flex', height: '100vh', justifyContent: 'center', alignItems: 'center', background: '#f4f5f7' }}>
          <div style={{ textAlign: 'center' }}>
            <div className="spin" style={{ fontSize: '24px', marginBottom: '10px' }}>⏳</div>
            <div style={{ fontSize: '16px', color: '#566070', fontWeight: '500' }}>Initializing Dashboard...</div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};
